import { useEffect, useRef, useState } from 'react';
import { useGetPaypalSandboxConfigQuery, useGetPaypalSandboxSellerQuery, useSyncPaypalSandboxSellerMutation, useOnboardPaypalSandboxMutation } from '../slices/paypalSandboxApiSlice';

export default function PaypalSandboxSeller() {
  const { data: config, error: configError, isLoading: configLoading, refetch: refetchConfig } = useGetPaypalSandboxConfigQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data, error, isFetching } = useGetPaypalSandboxSellerQuery(undefined, { skip: !config?.enabled, refetchOnMountOrArgChange: true });
  const [sync, { isLoading: syncing }] = useSyncPaypalSandboxSellerMutation();
  const [onboard, { isLoading: starting }] = useOnboardPaypalSandboxMutation();
  const [consent, setConsent] = useState(false);
  const [message, setMessage] = useState('');
  const ran = useRef(false);
  useEffect(() => {
    if (config?.enabled && new URLSearchParams(window.location.search).get('paypalSandbox') === 'return' && !ran.current) {
      ran.current = true;
      sync().unwrap().then(() => setMessage('PayPal status refreshed.')).catch(e => setMessage(e?.data?.message || 'Finish onboarding, then refresh status.'));
    }
  }, [config?.enabled, sync]);
  if (configError) return <section role="alert" className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
    <h3 className="text-xl font-semibold">PayPal sandbox</h3>
    <p>Could not load PayPal availability.</p>
    <button type="button" onClick={refetchConfig}>Retry</button>
  </section>;
  if (configLoading) return <p role="status">Checking PayPal availability…</p>;
  if (!config?.enabled) return null;
  const checking = isFetching || syncing;
  const ready = !error && data?.ready === true && !data?.seller?.revoked;
  const seller = data?.seller;
  const status = checking ? 'Checking onboarding status...' : error ? 'Onboarding status unavailable'
    : ready ? 'Onboarding complete' : seller?.revoked ? 'PayPal connection revoked'
    : seller?.merchantId ? 'Onboarding incomplete' : 'Not connected';
  return <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
    <h3 className="text-xl font-semibold">PayPal sandbox</h3>
    <p>Connect a PayPal sandbox seller account for test purchases. No real money is transferred.</p>
    <p role="status" className={`my-3 inline-block rounded-full px-3 py-1 text-sm font-semibold ${ready && !checking ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`}>{status}</p>
    {ready && !checking && <p>Ready to receive sandbox payments.</p>}
    {!error && !checking && seller?.merchantId && <dl className="my-3 text-sm">
      <dt className="font-semibold">Connected merchant</dt><dd>****{seller.merchantId.slice(-4)}</dd>
      <dt className="font-semibold">PayPal email confirmed</dt><dd>{seller.primaryEmailConfirmed ? 'Yes' : 'No'}</dd>
      <dt className="font-semibold">Payment and refund permissions granted</dt><dd>{seller.permissionsGranted ? 'Yes' : 'No'}</dd>
      <dt className="font-semibold">Payments enabled</dt><dd>{seller.paymentsReceivable ? 'Yes' : 'No'}</dd>
      {seller.checkedAt && <><dt className="font-semibold">Last checked with PayPal</dt><dd>{new Date(seller.checkedAt).toLocaleString()}</dd></>}
    </dl>}
    {!ready && !checking && !error && <><label className="flex items-start gap-2 mb-3"><input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
      I agree to share my sandbox account information with Practidex and continue to PayPal to grant payment and refund permissions.
    </label>
    <button type="button" disabled={!consent || starting || syncing} className="rounded border px-4 py-2 disabled:opacity-50" onClick={async () => {
      try { const result = await onboard().unwrap(); window.location.assign(result.redirectUrl); }
      catch (e) { setMessage(e?.data?.message || 'Could not start onboarding.'); }
    }}>{seller?.merchantId ? 'Continue PayPal onboarding' : 'Connect PayPal sandbox'}</button>{' '}</>}
    <button type="button" disabled={syncing || starting} className="rounded border px-4 py-2" onClick={async () => {
      try { await sync().unwrap(); setMessage('Status refreshed.'); } catch (e) { setMessage(e?.data?.message || 'Could not refresh status.'); }
    }}>Refresh PayPal status</button>
    <p role="status">{message || error?.data?.message}</p>
  </section>;
}
